import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, FlaskConical, X } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-sky-50 text-sky-700 border-sky-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  released: 'bg-slate-50 text-slate-700 border-slate-200',
};

const STATUS_LABELS = {
  pending: 'في الانتظار',
  completed: 'مكتمل',
  approved: 'معتمد',
  released: 'تم الإرسال',
};

const flagClass = (flag) => {
  if (flag === 'H') return 'bg-rose-100 text-rose-700';
  if (flag === 'L') return 'bg-sky-100 text-sky-700';
  if (flag === 'N') return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-600';
};

const flagLabel = (flag) => {
  if (flag === 'H') return 'مرتفع';
  if (flag === 'L') return 'منخفض';
  if (flag === 'N') return 'طبيعي';
  return '-';
};

const getDisplayValue = (comp) => {
  if (comp?.value_numeric !== null && comp?.value_numeric !== undefined) return comp.value_numeric;
  if (comp?.value_text !== null && comp?.value_text !== undefined && comp.value_text !== '') return comp.value_text;
  return '-';
};

const ResultsDetailsModal = ({ open, onClose, data, title = 'تفاصيل النتائج' }) => {
  if (!open || !data) return null;

  const booking = data.booking || {};
  const tests = data.tests || [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          dir="rtl"
        >
          <div className="bg-[#1E293B] px-6 py-5 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {title}
              </h3>
              <p className="text-slate-400 text-sm mt-1">
                {booking?.customer_id?.full_name || '-'} - <span className="font-mono">{booking?.barcode_no || '-'}</span>
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-100" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[72vh] space-y-4">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">معلومات المريض</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">الاسم</p>
                  <p className="font-semibold text-slate-900">{booking?.customer_id?.full_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">الهاتف</p>
                  <p className="font-semibold text-slate-900" dir="ltr">{booking?.customer_id?.phone || booking?.customer_id?.mobile || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">الفرع</p>
                  <p className="font-semibold text-slate-900">{booking?.branch_id?.branch_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">التاريخ</p>
                  <p className="font-semibold text-slate-900">
                    {booking?.booking_date ? new Date(booking.booking_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {tests.length === 0 ? (
              <p className="text-center text-slate-500 py-8">لا توجد تحاليل</p>
            ) : (
              <div className="space-y-3">
                {tests.map((test) => (
                  <div key={test._id} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <FlaskConical className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="font-semibold text-slate-900 text-sm truncate">
                          {test?.lab_test_id?.name_ar || test?.lab_test_id?.name_en || '-'}
                        </span>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${STATUS_STYLES[test?.result?.status || 'pending'] || STATUS_STYLES.pending}`}>
                        {STATUS_LABELS[test?.result?.status || 'pending'] || STATUS_LABELS.pending}
                      </span>
                    </div>

                    {Array.isArray(test?.result?.serial_results) && test.result.serial_results.length > 0 ? (
                      <div className="bg-white rounded-lg p-3 border border-slate-100 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                              <th className="text-right py-2">العنصر</th>
                              {test.result.serial_results.map((sr, idx) => (
                                <th key={idx} className="text-right py-2">{sr.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(test.result.serial_results[0]?.components || []).map((baseComp, ci) => (
                              <tr key={`${test._id}-sr-${ci}`} className="border-b border-slate-50 last:border-0">
                                <td className="py-2 text-slate-700">{baseComp?.component_name || '-'}</td>
                                {test.result.serial_results.map((sr, sIdx) => {
                                  const c = (sr.components || [])[ci];
                                  return (
                                    <td key={sIdx} className="py-2">
                                      <div className="font-semibold text-slate-900 tabular-nums">{getDisplayValue(c)}</div>
                                      <div className="text-[10px] text-slate-500">{sr.sample_collected_at ? new Date(sr.sample_collected_at).toLocaleString('en-GB') : '-'}</div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : Array.isArray(test?.result?.culture_grid_rows) && test.result.culture_grid_rows.length > 0 ? (
                      <div className="bg-white rounded-lg p-3 border border-slate-100 space-y-3">
                        {test.result.culture_grid_rows.map((row, rIdx) => (
                          <div key={rIdx} className="border border-slate-100 rounded-lg p-2">
                            <p className="text-sm font-semibold text-slate-800 mb-2">{row.organism_name}</p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-500 border-b border-slate-100">
                                  <th className="text-right py-1">Antibiotic</th>
                                  <th className="text-right py-1">Sensitivity</th>
                                  <th className="text-right py-1">MIC</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(row.antibiotics || []).map((ab, aIdx) => (
                                  <tr key={aIdx} className="border-b border-slate-50 last:border-0">
                                    <td className="py-1">{ab.antibiotic_name}</td>
                                    <td className="py-1">{ab.sensitivity}</td>
                                    <td className="py-1">{ab.mic_value || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    ) : test?.result?.rich_text_html ? (
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: test.result.rich_text_html }} />
                        {Array.isArray(test.result.rich_text_images) && test.result.rich_text_images.length > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                            {test.result.rich_text_images.map((img, idx) => (
                              <img key={idx} src={img.url} alt={img.caption || `img-${idx}`} className="w-full h-24 object-cover rounded border border-slate-200" />
                            ))}
                          </div>
                        )}
                      </div>
                    ) : test?.result?.components?.length > 0 ? (
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                              <th className="text-right py-2">العنصر</th>
                              <th className="text-right py-2">القيمة</th>
                              <th className="text-right py-2">الوحدة</th>
                              <th className="text-right py-2">الحالة</th>
                            </tr>
                          </thead>
                          <tbody>
                            {test.result.components.map((comp, i) => (
                              <tr key={`${test._id}-${i}`} className="border-b border-slate-50 last:border-0">
                                <td className="py-2 text-slate-700">{comp?.component_name || '-'}</td>
                                <td className="py-2 font-semibold text-slate-900 tabular-nums">{getDisplayValue(comp)}</td>
                                <td className="py-2 text-slate-600">{comp?.unit || '-'}</td>
                                <td className="py-2">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${flagClass(comp?.abnormal_flag)}`}>
                                    {flagLabel(comp?.abnormal_flag)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {test?.result?.result_comment && (
                          <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
                            <strong>ملاحظات:</strong> {test.result.result_comment}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 bg-white rounded-lg p-3 border border-slate-100">
                        لم يتم إدخال النتيجة بعد
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
            <button onClick={onClose} className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors">
              إغلاق
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ResultsDetailsModal;
